import React, { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { upsell, upsellDurationSeconds } from '../data/upsell';
import { useCountdown } from '../hooks/useCountdown';
type UpsellModalProps = {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
};
const EASE = [0.16, 1, 0.3, 1] as const;
export function UpsellModal({
  isOpen,
  onAccept,
  onDecline
}: UpsellModalProps) {
  const prefersReducedMotion = useReducedMotion();
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // No AnimatePresence and no exit transition on purpose: closing this modal is
  // always followed by a navigation, and the browser stops repainting once it
  // starts loading the next document — an exit animation would freeze on screen
  // as a half-faded ghost. It enters with motion and disappears instantly.
  if (!isOpen) return null;
  return <div className="upsell-root" data-id="element-157" data-mp="3lwj9mdvf78l">
      <motion.div className="upsell-scrim" onClick={onDecline} aria-hidden="true" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} data-id="element-158" data-mp="3kuzmtishcf9" />
      <motion.div initial={prefersReducedMotion ? {
      opacity: 0
    } : {
      y: '100%',
      opacity: 0
    }} animate={prefersReducedMotion ? {
      opacity: 1
    } : {
      y: 0,
      opacity: 1
    }} transition={{
      duration: prefersReducedMotion ? 0.2 : 0.32,
      ease: EASE
    }} className="relative flex w-full max-w-[560px] justify-center" data-id="element-159" data-mp="di9dqmh8pp4e">
        <UpsellSheet onAccept={onAccept} onDecline={onDecline} data-id="element-160" data-mp="3qrf9wly9vx22" />
      </motion.div>
    </div>;
}
type UpsellSheetProps = {
  onAccept: () => void;
  onDecline: () => void;
};
function UpsellSheet({
  onAccept,
  onDecline
}: UpsellSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const {
    minutes,
    seconds,
    isExpired
  } = useCountdown(upsellDurationSeconds);

  // Focus the sheet itself (never the CTA) so the panel always opens at the top.
  useEffect(() => {
    sheetRef.current?.focus({
      preventScroll: true
    });
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, []);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onDecline();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = sheetRef.current?.querySelectorAll<HTMLElement>('button');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  return <div ref={sheetRef} role="dialog" aria-modal="true" aria-labelledby="upsell-headline" tabIndex={-1} onKeyDown={handleKeyDown} className="upsell-sheet" data-id="element-161" data-mp="19h01m23lhfp4">
      <div className="upsell-head" data-id="element-162" data-mp="10iel0chooaqz">
        <p className="upsell-eyebrow" data-id="element-163" data-mp="10jg0qu9n9lf1">{upsell.eyebrow}</p>
        <button type="button" onClick={onDecline} aria-label="Chiudi e vai al pagamento" className="upsell-close" data-id="element-164" data-mp="15gzbvlb1pi2r">
          <XIcon style={{
          width: 20,
          height: 20
        }} aria-hidden="true" data-id="element-165" data-mp="1x742i1pef1cf" />
        </button>
      </div>

      <div ref={bodyRef} className="upsell-body" data-id="element-166" data-mp="1jgh64g741nji">
        <img src={upsell.image} alt={upsell.imageAlt} className="upsell-hero" width={1024} height={683} loading="eager" fetchPriority="high" decoding="sync" data-id="element-167" data-mp="1jfpa549lpnsp" />

        <h2 id="upsell-headline" className="upsell-headline" data-id="element-168" data-mp="2gvv1217bj1xk">
          Aggiungi il {upsell.title} per soli <span className="upsell-mark" data-id="element-169" data-mp="sa1rvirw2jcg">{upsell.price}</span>
        </h2>

        <p className="upsell-tagline" data-id="element-170" data-mp="s75wa13ik6r6">
          Il Mega Kit allena le mani.
          <br data-id="element-171" data-mp="n94ctf26e5f6" />
          Questo <span data-id="element-172" data-mp="ncs385cbqbpo">allena la testa</span>.
        </p>

        <ul className="upsell-copy" data-id="element-173" data-mp="j9n5eg36uy7d">
          <li data-id="element-174" data-mp="jaotbl5azf6i">
            <strong data-id="element-175" data-mp="e9r2yla53etx">Oltre 100 esercizi da stampare</strong> per l’attenzione, la memoria, la logica e
            la percezione visiva.
          </li>
          <li data-id="element-176" data-mp="1ayqce3mgmmnp">
            Basta <strong data-id="element-177" data-mp="1b24d77fh6nt1">una pagina al giorno</strong>. Dieci minuti. Niente schermi, niente app,
            niente da installare.
          </li>
          <li data-id="element-178" data-mp="1276grqd4cswy">
            <strong data-id="element-179" data-mp="126wsq8bmwvtw">Tre livelli inclusi</strong> (3-6, 7-10 e 11-14 anni): così va bene per tutti i
            bambini di casa.
          </li>
        </ul>
      </div>

      <div className="upsell-foot" data-id="element-180" data-mp="1uo0q5c2naxj6">
        <div className="upsell-summary" data-id="element-181" data-mp="1up2c4bl794fo">
          <p className="upsell-price" data-id="element-182" data-mp="1uq3pwntlo513">
            <span className="upsell-price-now" data-id="element-183" data-mp="1pwr0wcl0dw7t">{upsell.price}</span>
            <span className="upsell-price-was" data-id="element-184" data-mp="1pvfx4crkcogp">{upsell.comparePrice}</span>
          </p>

          {isExpired ? <p className="upsell-timer-ended" data-id="element-185" data-mp="2279yd1zf0q6n">Offerta in scadenza</p> : <p className="upsell-timer" data-id="element-186" data-mp="229cw686t7nlx">
              L’offerta scade tra{' '}
              <b data-id="element-187" data-mp="278pkyyh8r4r5">
                {minutes}:{seconds}
              </b>
            </p>}
        </div>

        <button type="button" onClick={onAccept} className="upsell-cta" data-id="element-188" data-mp="2b5veyrlbtv4s">
          {upsell.acceptLabel}
        </button>

        <p className="upsell-reassurance" data-id="element-189" data-mp="2l64fghmcq2pz">{upsell.reassurance}</p>

        <button type="button" onClick={onDecline} className="upsell-decline" data-id="element-190" data-mp="2l4b3qnwvssb4">
          {upsell.declineLabel}
        </button>
      </div>
    </div>;
}